import React from 'react';

// Format number to French locale (virgule as decimal separator)
const formatLocaleNumber = (value, decimals = 2) => {
    return Number(value || 0).toFixed(decimals).replace('.', ',');
};

export default function AnalyticsModal({ items = [], movements = [], computeCalculatedStock = () => 0, onClose }) {
    const CUTOFF_DATE = new Date('2026-07-01');
    const today = new Date();
    const isFriday = today.getDay() === 5;

    const getAnalytics = (item) => {
        if (!item || !item.id) return null;

        const itemMovements = (movements || []).filter((m) => {
            const movementTargetId = m?.stockable_id ?? m?.stockable?.id ?? m?.ingredient_id ?? m?.ingredient?.id ?? m?.item_id ?? m?.item?.id;
            return String(movementTargetId) === String(item.id);
        });

        const purchases = itemMovements.reduce((acc, m) => {
            const q = Number(m.quantity ?? m.qty ?? 0);
            if (q > 0) acc += q;
            return acc;
        }, 0);

        const consumptions = itemMovements.reduce((acc, m) => {
            const q = Number(m.quantity ?? m.qty ?? 0);
            if (q < 0) acc += Math.abs(q);
            return acc;
        }, 0);

        const daysElapsed = Math.max(1, Math.floor((today - CUTOFF_DATE) / (1000 * 60 * 60 * 24)));
        const avgInPerDay = purchases / daysElapsed;
        const avgOutPerDay = consumptions / daysElapsed;

        const stock = computeCalculatedStock(item);

        // Forecast: daily consumption = avgOutPerDay
        const dailyForecast = avgOutPerDay;

        // Weekend recommendation (Sat+Sun = 2 days)
        const weekendDays = 2;
        const projectedAfterWeekend = stock - avgOutPerDay * weekendDays;
        const purchaseToCoverWeekend = Math.max(0, Math.ceil(avgOutPerDay * weekendDays - stock + 1));

        // Recommend only on Fridays for weekend purchases, but still provide the number
        const recommendNow = isFriday && purchaseToCoverWeekend > 0;

        return {
            purchases,
            consumptions,
            avgInPerDay,
            avgOutPerDay,
            stock,
            dailyForecast,
            projectedAfterWeekend,
            purchaseToCoverWeekend,
            recommendNow,
        };
    };

    const analytics = items.map((item) => ({ item, stats: getAnalytics(item) }));

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4" onClick={onClose}>
            <div className="mx-auto flex min-h-full items-center justify-center">
                <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between border-b border-green-200 p-6">
                            <h2 className="text-2xl font-semibold text-green-900">Vue synthétique par article</h2>
                            <button type="button" onClick={onClose} className="rounded-full border border-green-200 px-3 py-1 text-2xl text-green-600">×</button>
                        </div>

                        <div className="overflow-auto p-6">
                            <p className="mb-4 text-sm text-green-600">Moyennes sur la période depuis le 01/07/2026. La recommandation d'achat prend en compte le week-end si aujourd'hui est un vendredi.</p>
                            <table className="min-w-full divide-y divide-green-200 text-left text-sm text-slate-700">
                                <thead className="bg-green-100 text-green-900">
                                    <tr>
                                        <th className="px-4 py-3">Nom</th>
                                        <th className="px-4 py-3">Stock actuel</th>
                                        <th className="px-4 py-3">Entrées / jour</th>
                                        <th className="px-4 py-3">Sorties / jour</th>
                                        <th className="px-4 py-3">Prévision journalière</th>
                                        <th className="px-4 py-3">Recommandation (week-end)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-green-100 bg-white">
                                    {analytics.map(({ item, stats }) => (
                                        <tr key={item.id} className="hover:bg-slate-50 even:bg-white">
                                            <td className="px-4 py-3 font-medium text-slate-900">{item.name}</td>
                                            <td className="px-4 py-3">{formatLocaleNumber(stats.stock, 0)} {item.unit || ''}</td>
                                            <td className="px-4 py-3">{formatLocaleNumber(stats.avgInPerDay, 2)}</td>
                                            <td className="px-4 py-3">{formatLocaleNumber(stats.avgOutPerDay, 2)}</td>
                                            <td className="px-4 py-3">{formatLocaleNumber(stats.dailyForecast, 2)}</td>
                                            <td className="px-4 py-3">
                                                {isFriday ? (
                                                    stats.purchaseToCoverWeekend > 0 ? (
                                                        <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">Commander {formatLocaleNumber(stats.purchaseToCoverWeekend, 0)} {item.unit || ''}</span>
                                                    ) : (
                                                        <span className="text-sm text-green-600">Pas d'achat nécessaire</span>
                                                    )
                                                ) : (
                                                    <span className="text-sm text-green-600">Disponible (reco activée le vendredi)</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex-shrink-0 border-t border-green-200 p-6">
                            <div className="flex justify-end">
                                <button type="button" onClick={onClose} className="rounded-3xl bg-green-600 px-6 py-2 font-semibold text-white hover:bg-green-700">Fermer</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
